import React, { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import styled, { keyframes } from 'styled-components'
import { useApi } from '../../../hooks/useApi'

interface LogGroup {
  creationTime: number
  arn: string
  logGroupClass: string
  logGroupName: string
  metricFilterCount: number
}

interface Data {
  logGroups: LogGroup[]
  nextToken: string
}

const LogsStreams: React.FC = () => {
  const { callApi } = useApi()
  const navigate = useNavigate()
  const location = useLocation()
  const [logsData, setLogsStreams] = useState<LogGroup[] | null>(null)
  const [next, setNext] = useState<string | null>(null)
  const [button, setButton] = useState({ disabled: false, message: 'See more' })

  const fetchData = async () => {
    try {
      const response = await callApi('/admin/logs')
      setLogsStreams(response.logGroups)
      setNext(response.nextToken || null)
    } catch (error: any) {
      console.error('Token list error:', error)
    }
  }

  useEffect(() => { fetchData() }, [])

  const handleRowClick = (logGroupName: string) => {
    navigate(`${location.pathname}/groups?name=${logGroupName}`, { replace: true })
  }

  const handleSeeMore = async () => {
    try {
      const response = await callApi('/admin/logs', { nextToken: next })
      if (response.logGroups && response.logGroups.length > 0) {
        setLogsStreams(logsData ? [...logsData, ...(response.logGroups ?? [])] : response.logGroups)
        setNext(response.nextToken || null)
      } else if (response?.logGroups?.length === 0) {
        setButton({ disabled: true, message: 'No more logs' })
      }
    } catch (error) {
      console.error('There was an error loading more logs:', error)
    }
  }

  if (!logsData) return <LoaderBar />

  return (
    <Wrap>
      <TableWrap>
        <Table>
          <thead>
            <tr>
              <Th>Name</Th>
              <Th>Arn</Th>
              <Th>Created at</Th>
              <Th>Class</Th>
              <Th>Metric filters</Th>
            </tr>
          </thead>
          <tbody>
            {logsData.map((item, index) => (
              <tr key={index} onClick={() => handleRowClick(item.logGroupName)} style={{ cursor: 'pointer' }}>
                <Td><GroupName>{item.logGroupName}</GroupName></Td>
                <Td><Mono>{item.arn}</Mono></Td>
                <Td><Mono>{new Date(item.creationTime).toLocaleString()}</Mono></Td>
                <Td>{item.logGroupClass}</Td>
                <Td><Mono>{item.metricFilterCount}</Mono></Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableWrap>
      {next && (
        <SeeMoreBtn onClick={handleSeeMore} disabled={button.disabled}>
          {button.message}
        </SeeMoreBtn>
      )}
    </Wrap>
  )
}

export default LogsStreams

// ─── Styles ──────────────────────────────────────────────────────────────────

const Wrap = styled.div`display: flex; flex-direction: column; gap: 12px;`

const shimmer = keyframes`0% { transform: translateX(-100%); } 100% { transform: translateX(100%); }`
const LoaderBar = styled.div`
  height: 2px; background: rgba(255,255,255,0.06); border-radius: 2px; overflow: hidden; position: relative;
  &::after { content: ''; display: block; position: absolute; inset: 0; background: linear-gradient(90deg, transparent, rgba(111,92,230,0.5), transparent); animation: ${shimmer} 1.4s ease infinite; }
`

const TableWrap = styled.div`overflow-x: auto; border: 1px solid rgba(255,255,255,0.07); border-radius: 8px;`
const Table = styled.table`width: 100%; border-collapse: collapse; font-size: 13px;`
const Th = styled.th`padding: 10px 12px; text-align: left; color: #a1a1a1; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; border-bottom: 1px solid rgba(255,255,255,0.06);`
const Td = styled.td`padding: 8px 10px; color: #a0a0a0; font-size: 13px; vertical-align: middle; border-bottom: 1px solid rgba(255,255,255,0.04); tr:last-child & { border-bottom: none; }`
const Mono = styled.span`font-family: 'SF Mono','Fira Code',monospace; font-size: 11px; color: #777;`
const GroupName = styled.span`font-family: 'SF Mono','Fira Code',monospace; font-size: 12px; color: #a89aff;`

const SeeMoreBtn = styled.button`
  height: 28px; padding: 0 12px; background: rgba(111,92,230,0.1); border: 1px solid rgba(111,92,230,0.3);
  border-radius: 5px; color: #a89aff; font-size: 12px; font-weight: 500; cursor: pointer; align-self: flex-start;
  transition: background 0.15s; &:hover:not(:disabled) { background: rgba(111,92,230,0.2); border-color: rgba(111,92,230,0.5); }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`
